import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home,
  Globe,
  Brain,
  Grid3x3 
} from 'lucide-react';
import { cn } from '../../utils/cn'

interface TabItem {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

const tabs: TabItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/',
    icon: Home,
  },
  {
    id: 'network',
    label: 'Network',
    path: '/network',
    icon: Globe,
  },
  {
    id: 'ai-mcp',
    label: 'AI-MCP',
    path: '/ai-mcp',
    icon: Brain,
  },
  {
    id: 'more',
    label: 'More',
    path: '#more',
    icon: Grid3x3,
  },
];

interface BottomTabBarProps {
  onMoreClick: () => void;
  className?: string;
}

export function BottomTabBar({ onMoreClick, className }: BottomTabBarProps) {
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleTabClick = (tab: TabItem, e: React.MouseEvent) => {
    if (tab.id === 'more') {
      e.preventDefault();
      onMoreClick();
    }
  };

  return (
    <>
      {/* Bottom Tab Bar - Only visible on mobile */}
      <nav 
        className={cn(
          "fixed bottom-0 left-0 right-0 z-[100] lg:hidden",
          "bg-gray-900/90 backdrop-blur-md border-t border-gray-800",
          "safe-area-bottom",
          className
        )}
      >
        <div className="flex items-center justify-around h-[56px]">
          {tabs.map((tab) => {
            const active = tab.id !== 'more' && isActive(tab.path);
            const Icon = tab.icon;
            
            const content = (
              <div className="relative flex flex-col items-center justify-center min-w-[64px] h-full">
                <div className="relative">
                  <Icon 
                    className={cn(
                      "w-6 h-6 transition-colors",
                      active ? "text-accent-blue" : "text-gray-400"
                    )} 
                  />
                  {tab.badge && tab.badge > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
                      {tab.badge > 9 ? '9+' : tab.badge}
                    </span>
                  )}
                </div>
                <span 
                  className={cn(
                    "text-[10px] mt-1 transition-colors",
                    active ? "text-accent-blue font-medium" : "text-gray-400"
                  )}
                >
                  {tab.label}
                </span>
                {active && (
                  <div className="absolute inset-0 bg-accent-blue/10 rounded-lg -z-10" />
                )}
              </div>
            );

            if (tab.id === 'more') {
              return (
                <button
                  key={tab.id}
                  onClick={(e) => handleTabClick(tab, e)}
                  className="flex-1 touch-target focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                  aria-label={tab.label}
                >
                  {content}
                </button>
              );
            }

            return (
              <Link
                key={tab.id}
                to={tab.path}
                className="flex-1 touch-target focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                aria-label={tab.label}
                aria-current={active ? 'page' : undefined}
              >
                {content}
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}